import { useEffect } from 'react';

/** The surfaces that light up under the pointer. */
const SPOTLIT = '.pa-card, .pa-tile';

/**
 * Lights the card under the pointer — the "site" design system's spotlight (`styles/site.css`).
 *
 * One listener for the whole portal, not one per card: it finds the card under the pointer and writes
 * its local position as `--mx`/`--my`, at most once per frame. The CSS paints a soft blue glow there.
 * Touch-only devices and reduced motion get nothing, and the cards render normally.
 *
 * @param enabled Only while the site theme is on screen.
 */
export function useCardSpotlight(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof window.matchMedia !== 'function') return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let frame = 0;
    let event: PointerEvent | null = null;
    let lit: HTMLElement | null = null;

    const paint = () => {
      frame = 0;
      if (!event) return;
      const card = (event.target as Element | null)?.closest?.<HTMLElement>(SPOTLIT) ?? null;
      if (lit && lit !== card) {
        lit.style.removeProperty('--mx');
        lit.style.removeProperty('--my');
      }
      lit = card;
      if (!card) return;
      const box = card.getBoundingClientRect();
      card.style.setProperty('--mx', `${(event.clientX - box.left).toFixed(0)}px`);
      card.style.setProperty('--my', `${(event.clientY - box.top).toFixed(0)}px`);
    };

    const onMove = (next: PointerEvent) => {
      event = next;
      if (!frame) frame = window.requestAnimationFrame(paint);
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      if (frame) window.cancelAnimationFrame(frame);
      lit?.style.removeProperty('--mx');
      lit?.style.removeProperty('--my');
    };
  }, [enabled]);
}
